const { chargeCode, COMPUTATION_TYPES } = require('pando-constants');
const convertWeight = require('../../helpers/weightConverter');
const convertVolume = require('../../helpers/volumeConverter');
const { freightUnit } = require('../../constants/payments');
const Helper = require('./utils/helper');

const PACKAGE_UNITS = [freightUnit.per_package, freightUnit.per_case, freightUnit.per_box];

class CustomDestinationCharge {
  calculateQuantity(materials = [], chargeUnit = '', materialMap, quantityUnitsForPieces) {
    if (!materials || !materials.length) return 0;
    if (chargeUnit === freightUnit.per_trip) return 1;
    if (chargeUnit === freightUnit.per_ton) {
      return materials.reduce((acc, m) => acc + convertWeight(m.weightUnit, 't', m.weight), 0);
    }
    if (chargeUnit === freightUnit.per_kg) {
      return materials.reduce((acc, m) => acc + convertWeight(m.weightUnit, 'kg', m.weight), 0);
    }
    if (chargeUnit === freightUnit.per_cft) {
      return materials.reduce((acc, m) => acc + convertVolume(m.volumeUnit, 'cft', m.volume), 0);
    }
    if (PACKAGE_UNITS.includes(chargeUnit)) {
      return materials.reduce((acc, m) => {
        const qty = Math.ceil(m.quantity || 0);
        const quantityUnit = m.quantityUnit && m.quantityUnit.toLowerCase();
        if (quantityUnit && quantityUnitsForPieces[quantityUnit]) {
          const detail = materialMap[m.materialCode];
          return detail && detail.pce_box ? acc + Math.ceil(qty / Math.ceil(detail.pce_box)) : acc;
        }
        return acc + qty;
      }, 0);
    }
    return 0;
  }

  computeChargeValue(chargeItem = {}, materials = [], materialMap = {}, quantityUnitsForPieces = {}) {
    const { charge = 0, min_charge = 0, charge_unit: chargeUnit } = chargeItem;
    const quantity = this.calculateQuantity(materials, chargeUnit, materialMap, quantityUnitsForPieces);
    const computed = chargeUnit === freightUnit.per_trip ? charge : charge * quantity;
    return Math.max(computed, min_charge);
  }

  perform(masterData = {}, strategy = []) {
    const {
      indentDestinations = [],
      indentDestinationCharges = [],
      destinations = [],
      clientConfiguration = {},
      materials: { masterData: materialMasterData = [] } = {},
      customQuantityUnits,
    } = masterData;

    const materialMap = Helper.fetchMaterialMap(materialMasterData);
    const quantityUnitsForPieces = Helper.getQuantityUnitsForPieces(customQuantityUnits);
    const chargeComputeMethodMap = new Map();
    const chargesHash = new Map();
    const chargeOutputHash = {};
    const stdDestinationCharges = Object.values(chargeCode);
    const chargeCodeMap = {};

    (clientConfiguration.allowable_destination_charge_codes || []).forEach((charge) => {
      chargeComputeMethodMap.set(charge.name, charge.compute_method);
      chargeCodeMap[charge.name] = charge;
    });

    indentDestinationCharges.forEach(({ destination_id, charges = [], charge_code }) => {
      if (destination_id && !stdDestinationCharges.includes(charge_code) && strategy.includes(charge_code)) {
        const key = `${destination_id}`;
        if (!chargesHash.has(key)) {
          chargesHash.set(key, []);
        }
        chargesHash.get(key).push({ charges, charge_code });
      }
    });

    indentDestinations.forEach(({ gateId, locationId }) => {
      if (!locationId && !gateId) return;
      const key = gateId ? `${gateId}` : `${locationId}`;
      const chargesList = chargesHash.get(key);

      if (chargesList) {
        const destMaterials = destinations
          .filter(d => d.locationId && d.locationId.toString() === (locationId || gateId).toString())
          .flatMap(d => Helper.getNormalizedMaterialDetails(d.materials));

        chargesList.forEach((charge) => {
          const values = (charge.charges || []).map(c =>
            this.computeChargeValue(c, destMaterials, materialMap, quantityUnitsForPieces));
          const computeMethod = chargeComputeMethodMap.get(charge.charge_code);

          if (computeMethod === COMPUTATION_TYPES.MAX) {
            chargeOutputHash[charge.charge_code] = Math.max(chargeOutputHash[charge.charge_code] || 0, ...values, 0);
          } else if (computeMethod === COMPUTATION_TYPES.ADD) {
            chargeOutputHash[charge.charge_code] = (chargeOutputHash[charge.charge_code] || 0) + values.reduce((a, b) => a + b, 0);
          }
        });
      }
    });

    this.chargeOutputHash = Object.keys(chargeOutputHash).map((name) => {
      const charge = chargeCodeMap[name];
      return {
        name,
        label: charge.label,
        amount: chargeOutputHash[name],
        master_type: 'Destination',
      };
    });
    return this.chargeOutputHash;
  }
}
module.exports = CustomDestinationCharge;
